// @ts-nocheck
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMantineTheme, useMantineColorScheme } from '@mantine/core';
import { Stack, Center, Container } from '@/components/ui/layout';
import { Card } from '@/components/ui/data-display';
import { Title, Text, Button, Anchor, Badge } from '@/components/ui/core';
import { TextInput } from '@/components/ui/inputs';
import { Alert } from '@/components/ui/feedback';
import { IconMail, IconArrowLeft, IconKey, IconCheck, IconCube } from '@tabler/icons-react';
import { FadeIn, SlideIn, HoverScale } from '@/components/animations';
import { ParallaxBackground } from '../components';

export default function ForgotParallax() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <ParallaxBackground preset="waves" mouseParallax intensity={25}>
      <Center mih="100vh" p="md">
        <Container size="xs" w="100%">
          <SlideIn direction="up" delay={100} duration={600}>
            <Card
              p="xl"
              radius="lg"
              style={{
                backgroundColor: isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(16px)',
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
              }}
            >
              {submitted ? (
                <Stack gap="lg" align="center">
                  <FadeIn delay={100}>
                    <Center
                      style={{
                        width: 80,
                        height: 80,
                        borderRadius: 20,
                        backgroundColor: 'var(--mantine-color-green-light)',
                      }}
                    >
                      <IconCheck size={40} color="var(--mantine-color-green-6)" />
                    </Center>
                  </FadeIn>

                  <FadeIn delay={200}>
                    <Stack align="center" gap="xs">
                      <Title order={2} ta="center">Check your email</Title>
                      <Text c="dimmed" ta="center" maw={300}>
                        We've sent a password reset link to <strong>{email}</strong>
                      </Text>
                    </Stack>
                  </FadeIn>

                  <FadeIn delay={300}>
                    <Alert color="blue" variant="light" w="100%">
                      <Text size="sm">
                        Didn't receive the email? Check your spam folder or{' '}
                        <Anchor onClick={() => setSubmitted(false)}>try again</Anchor>
                      </Text>
                    </Alert>
                  </FadeIn>

                  <FadeIn delay={400}>
                    <Button
                      component={Link}
                      to="/app/demo/auth-pages/login/parallax"
                      variant="subtle"
                      leftSection={<IconArrowLeft size={16} />}
                    >
                      Back to login
                    </Button>
                  </FadeIn>
                </Stack>
              ) : (
                <Stack gap="lg">
                  <Button
                    component={Link}
                    to="/app/demo/auth-pages/forgot-password"
                    variant="subtle"
                    size="xs"
                    leftSection={<IconArrowLeft size={14} />}
                    w="fit-content"
                  >
                    Back to variants
                  </Button>

                  <FadeIn delay={200}>
                    <Stack align="center" gap="xs">
                      <Badge
                        leftSection={<IconCube size={12} />}
                        variant="gradient"
                        gradient={{ from: 'blue', to: 'cyan' }}
                        size="lg"
                      >
                        Parallax Effect
                      </Badge>
                      <Center
                        style={{
                          width: 56,
                          height: 56,
                          borderRadius: 14,
                          backgroundColor: 'var(--mantine-color-blue-light)',
                          marginTop: 'var(--mantine-spacing-sm)',
                        }}
                      >
                        <IconKey size={28} color="var(--mantine-color-blue-6)" />
                      </Center>
                      <Title order={2} ta="center" mt="sm">
                        Forgot password?
                      </Title>
                      <Text c="dimmed" size="sm" ta="center" maw={300}>
                        No worries, we'll send you reset instructions.
                      </Text>
                    </Stack>
                  </FadeIn>

                  <form onSubmit={handleSubmit}>
                    <Stack gap="md">
                      <FadeIn delay={300}>
                        <TextInput
                          label="Email"
                          placeholder="Enter your email"
                          leftSection={<IconMail size={16} />}
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          size="md"
                          radius="md"
                        />
                      </FadeIn>

                      <FadeIn delay={400}>
                        <HoverScale scale={1.02}>
                          <Button
                            type="submit"
                            fullWidth
                            size="md"
                            radius="md"
                            variant="gradient"
                            gradient={{ from: 'blue', to: 'cyan' }}
                          >
                            Send reset link
                          </Button>
                        </HoverScale>
                      </FadeIn>
                    </Stack>
                  </form>

                  <FadeIn delay={500}>
                    <Center>
                      <Anchor component={Link} to="/app/demo/auth-pages/login/parallax" size="sm">
                        <IconArrowLeft size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                        Back to login
                      </Anchor>
                    </Center>
                  </FadeIn>
                </Stack>
              )}
            </Card>
          </SlideIn>
        </Container>
      </Center>
    </ParallaxBackground>
  );
}
